import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { ReconciliationService } from "@/api";
import type {
  reconciliation_CandidateListResponse,
  reconciliation_ConfirmReviewItemRequest,
  reconciliation_CreditedReviewQueueListResponse,
  reconciliation_IgnoredReviewQueueListResponse,
  reconciliation_ReviewQueueListResponse,
  reconciliation_DuplicateRevertResponse,
  reconciliation_RevertResponse,
  reconciliation_SearchReconciliationResponse,
} from "@/api";
import { invalidateMoneyViews } from "./invalidateMoneyViews";

/**
 * "duplicates" (migration 180) lists live import rows that share QR reference,
 * amount and booking day with another live row — the two copies of one bank
 * booking that slipped past the dedupe. Same response shape as "credited".
 */
export type ReviewQueueStatusFilter =
  | "active"
  | "ignored"
  | "credited"
  | "duplicates";

export interface ReviewQueueListFilters {
  limit?: number;
  offset?: number;
  status?: ReviewQueueStatusFilter;
  /**
   * Optional search term applied server-side to amount, QR-Reference and
   * remittance info ("Payment reference"). Empty string disables filtering.
   * Caller should debounce input before passing it through.
   */
  search?: string;
}

export const reviewQueueKeys = {
  all: ["reconciliation", "review-queue"] as const,
  list: (filters: ReviewQueueListFilters) =>
    [...reviewQueueKeys.all, "list", filters] as const,
  candidates: (paymentId: string | null) =>
    [...reviewQueueKeys.all, "candidates", paymentId] as const,
};

/**
 * Lists review-queue items. The backend's /review-queue endpoint switches its
 * response shape based on the `status` query param: the default (`active`)
 * returns the unapplied-payment list with candidates; `ignored` returns the
 * Restore view (bank_transactions with their ignore reasons); `credited`
 * returns the recently confirmed view (bank_transactions in status='matched'
 * with action attribution). The OpenAPI generator picks one shape as the
 * declared return type, so this hook accepts the union — callers narrow on
 * the `status` filter they passed.
 *
 * FRESHNESS POLICY (#1338) — see the FRESHNESS POLICY block at the top of
 * `useDebtorAccountsList.ts` for the reasoning; this records this hook's call.
 *
 * Focus refetch is ON, and this is the surface in the set that needs it most.
 * The queue is shared work: two operators clearing it at once, or an import
 * that lands while a tab sits in the background, both leave this list offering
 * a payment that is already gone.
 *
 * The load objection that keeps the org-wide cash-in register off does not
 * apply. `ListPaymentsForReviewQueue` is filtered to
 * `p.status IN ('unapplied','partially_applied')` — the working set an
 * operator is actively clearing, which shrinks as they work. It is not a scan
 * over payment history and it does not grow with billing cycles.
 *
 * staleTime is written out rather than inherited so the window reads as
 * chosen. The value is the app default and is unchanged by this issue.
 */
export function useReviewQueueList(filters: ReviewQueueListFilters = {}) {
  const search = filters.search?.trim() ?? "";
  return useQuery<
    | reconciliation_ReviewQueueListResponse
    | reconciliation_IgnoredReviewQueueListResponse
    | reconciliation_CreditedReviewQueueListResponse
  >({
    queryKey: reviewQueueKeys.list({ ...filters, search }),
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: true,
    queryFn: () =>
      ReconciliationService.getApiV1ReconciliationReviewQueue(
        filters.status,
        search === "" ? undefined : search,
        filters.limit,
        filters.offset,
      ),
    // Keep the previous page only while the status tab stays the same. The
    // response shape switches with the status, and a Credited page shown as
    // placeholder under the Duplicates tab would label ordinary credited rows
    // as duplicates and offer their removal before the real list arrives.
    placeholderData: (previousData, previousQuery) =>
      (previousQuery?.queryKey[3] as ReviewQueueListFilters | undefined)
        ?.status === filters.status
        ? previousData
        : undefined,
  });
}

/**
 * Candidate debtor accounts for one queued payment, rendered as the ordered
 * card list in ReviewCandidatePanel.
 *
 * FRESHNESS POLICY (#1338) — see the FRESHNESS POLICY block at the top of
 * `useDebtorAccountsList.ts` for the reasoning; this records this hook's call.
 *
 * Focus refetch is OFF, and here that is a CORRECTNESS decision rather than
 * the load argument that keeps the org-wide register off. This list is
 * ordered, and the operator acts on it by position: the keyboard `a` shortcut
 * in ReviewCandidatePanel confirms the card at index 0. Refetching when the
 * operator returns to the tab is precisely the moment they are about to press
 * it, and a re-ranked or re-fetched list can put a different debtor at index 0
 * than the one that was on screen when they left. Applying a payment to the
 * wrong family is a worse failure than showing a candidate set a few minutes
 * old, and the set is a suggestion the operator is about to confirm anyway,
 * not a number they will read as fact.
 *
 * Staleness is still bounded: `invalidateMoneyViews` drops
 * `reviewQueueKeys.all` on every billing-domain write, in this tab and across
 * tabs, and selecting a different payment fetches a fresh set for it.
 */
export function useReviewQueueCandidates(paymentId: string | null) {
  return useQuery<reconciliation_CandidateListResponse>({
    queryKey: reviewQueueKeys.candidates(paymentId),
    queryFn: () =>
      ReconciliationService.getApiV1ReconciliationReviewQueueCandidates(
        paymentId ?? "",
      ),
    enabled: paymentId !== null && paymentId !== "",
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export interface ConfirmReviewItemInput {
  // Named `paymentId` (not `transactionId`) because /review-queue/:id/confirm
  // resolves :id as a payment id, not a bank-transaction id. The sibling
  // /revert and /ignore endpoints share the URL pattern but resolve a
  // bank-transaction id — confusing the two shipped a real bug (issue 484).
  paymentId: string;
  request: reconciliation_ConfirmReviewItemRequest;
}

export function useConfirmReviewItem() {
  const queryClient = useQueryClient();
  return useMutation<
    Record<string, string>,
    unknown,
    ConfirmReviewItemInput
  >({
    mutationFn: ({ paymentId, request }) =>
      ReconciliationService.postApiV1ReconciliationReviewQueueConfirm(
        paymentId,
        request,
      ),
    onSuccess: async () => {
      // A confirm applies the payment to invoice splits and/or bumps the
      // debtor's unapplied_credit_cents. Every surface that shows either —
      // the queue, the batch, the debtor slide-over, the invoice paid status,
      // the account statement — is in the one shared list, which also reaches
      // the operator's other browser tabs.
      await invalidateMoneyViews(queryClient);
    },
  });
}

export interface IgnoreTransactionInput {
  transactionId: string;
  reason?: string;
}

/**
 * Persistently ignores a bank transaction so the matcher stops re-evaluating
 * it and the active review queue hides it. Idempotent on repeat calls.
 */
export function useIgnoreTransaction() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, IgnoreTransactionInput>({
    mutationFn: ({ transactionId, reason }) =>
      ReconciliationService.postApiV1ReconciliationReviewQueueIgnore(
        transactionId,
        { reason: reason ?? "" },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewQueueKeys.all });
    },
  });
}

export interface UnignoreTransactionInput {
  transactionId: string;
}

/**
 * Restores a previously ignored bank transaction so the matcher re-evaluates
 * it on the next run. Powers the Undo toast and the Restore button on the
 * Ignored view of the queue.
 */
export function useUnignoreTransaction() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, UnignoreTransactionInput>({
    mutationFn: ({ transactionId }) =>
      ReconciliationService.postApiV1ReconciliationReviewQueueUnignore(
        transactionId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: reviewQueueKeys.all });
    },
  });
}

export interface RevertCreditedTransactionInput {
  transactionId: string;
  /**
   * Operator-supplied justification for the revert. Persisted on
   * payment_applications.revert_reason and surfaced as an
   * `application_reverted` entry in the debtor ledger (issue #591).
   * Required, min 3 / max 500 chars.
   */
  reason: string;
}

/**
 * Reverses every operator-driven payment_application attached to the
 * bank transaction's payment(s) (issue 484). Surfaces a structured
 * FIFODriftResponse on 409 — caller can read `error.body.code === 'fifo_drift'`
 * and render the consuming-applications list.
 *
 * On success, invalidates the review-queue, debtor-account, payment-
 * applications and import-batch caches so the Active/Credited tabs, the
 * Invoice "View payments" side-sheet, and the debtor ledger all refetch
 * and reflect the post-undo state.
 */
export interface RevertDuplicateBankTransactionInput {
  transactionId: string;
  /** Stored as the bank transaction's ignore reason. Min 3 / max 500 chars. */
  reason: string;
}

/**
 * Full unwind of ONE imported bank transaction that was booked twice
 * (POST /review-queue/:id/revert-duplicate). Unlike the credited Undo it
 * reverts auto-match and credit-carry rows too, re-covers every reopened
 * invoice from the family's remaining credit and leaves the row ignored so
 * the same file can be re-uploaded without resurrecting the copy.
 */
export function useRevertDuplicateBankTransaction() {
  const queryClient = useQueryClient();
  return useMutation<
    reconciliation_DuplicateRevertResponse,
    unknown,
    RevertDuplicateBankTransactionInput
  >({
    mutationFn: ({ transactionId, reason }) =>
      ReconciliationService.postApiV1ReconciliationReviewQueueRevertDuplicate(
        transactionId,
        { reason: (reason ?? "").trim() },
      ),
    onSuccess: async () => {
      await invalidateMoneyViews(queryClient);
    },
  });
}

export function useRevertCreditedTransaction() {
  const queryClient = useQueryClient();
  return useMutation<
    reconciliation_RevertResponse,
    unknown,
    RevertCreditedTransactionInput
  >({
    mutationFn: ({ transactionId, reason }) =>
      ReconciliationService.postApiV1ReconciliationReviewQueueRevert(
        transactionId,
        // Normalize at the boundary: whitespace-only reasons collapse to ""
        // so the backend's min-3 contract (issue #591) rejects them instead
        // of persisting a blank revert_reason.
        { reason: (reason ?? "").trim() },
      ),
    onSuccess: async () => {
      // A revert unwinds applications on invoice splits and returns credit to
      // the debtor pool — the same set of surfaces as the confirm above.
      await invalidateMoneyViews(queryClient);
    },
  });
}

/**
 * useReconciliationSearch hits the unified search endpoint
 * (GET /api/v1/reconciliation/search) and returns the same debtor-grouped
 * candidate DTO the auto-match path emits, so the FE can render results in
 * DebtorGroupedCandidateCard. Disabled while the trimmed query is shorter
 * than the backend's minimum (2 characters); a 30s staleTime caches
 * repeated typing without re-firing the network.
 */
export function useReconciliationSearch(searchTerm: string, limit = 20) {
  return useQuery<reconciliation_SearchReconciliationResponse>({
    queryKey: ["reconciliation", "search", searchTerm, limit],
    queryFn: () =>
      ReconciliationService.getApiV1ReconciliationSearch(
        searchTerm.trim(),
        limit,
      ),
    enabled: searchTerm.trim().length >= 2,
    staleTime: 30_000,
  });
}
